import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { ReportContextSchema } from '../report-context/report-context';
import { ReportReasonCodeSchema } from '../report-reason/report-reason';
import { ReportResolutionSchema } from '../report-resolution/report-resolution';
import { ReportStatusSchema } from '../report-status/report-status';

/**
 * What the **reporter** is told after filing.
 *
 * Deliberately thin, and it never names the reported member's account beyond the
 * id the reporter already sent. A receipt exists so the sheet can say "we have
 * this" and so a repeat report reads as already-done rather than as a second
 * filing — it is **not** a window onto the review. The reporter learns nothing
 * about what staff decided, because telling them would make the queue a way to
 * probe whether somebody was actioned.
 */
export const ReportReceiptSchema = z
  .object({
    id: UuidSchema,
    reportedUserId: UuidSchema,
    reason: ReportReasonCodeSchema,
    status: ReportStatusSchema,
    reportedAt: z.iso.datetime(),
  })
  .strict();

export type ReportReceipt = z.infer<typeof ReportReceiptSchema>;

/**
 * A report as **staff** see it — one shape for the queue row and the detail.
 *
 * One schema rather than a row plus a detail, because there is nothing a
 * reviewer may see on one and not the other: everything here is already inside
 * the `moderation.review` permission, and a second shape would be a second place
 * for a field to be forgotten. `SignalMatch`'s split exists because its detail
 * carries the *member's other signals*; a report has no such second query.
 *
 * **No member content travels on this shape** — not the photos, not the answers,
 * not the messages. A reviewer opens those through the surfaces that already
 * compose them under their own permissions and their own audit (N12: every admin
 * view of ID media is logged). Copying them onto the queue row would put member
 * data in a list response, and a list response is the thing that gets cached,
 * paged and exported.
 */
export const ReportSchema = z
  .object({
    id: UuidSchema,
    reporterUserId: UuidSchema,
    reportedUserId: UuidSchema,
    reason: ReportReasonCodeSchema,
    context: ReportContextSchema,
    /** The conversation to open, for a report raised inside one. */
    contextRef: UuidSchema.nullable(),
    /** The reporter's own words. Present only when they wrote any. */
    note: z.string().nullable(),
    status: ReportStatusSchema,
    resolution: ReportResolutionSchema.nullable(),
    /** The reviewer's own note — staff-facing; no member ever reads it. */
    resolutionNote: z.string().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    resolvedByUserId: UuidSchema.nullable(),
    reportedAt: z.iso.datetime(),
    /**
     * How many reports are open against **this reported member**, this row
     * included.
     *
     * Carried on the row rather than fetched per row: it is the number that
     * decides whether a reviewer treats one complaint or a pattern, and a queue
     * that made a request per row to learn it would be an N+1 across the page
     * (P14b). Computed in the list's own aggregating statement.
     *
     * It is also the number `moderation.autoHideReportThreshold` compares
     * against, which is why it counts **open** reports specifically — the same
     * predicate the partial unique index is built from.
     */
    openReportsAgainstMember: z.number().int().min(0),
  })
  .strict();

export type Report = z.infer<typeof ReportSchema>;
